import type { CriticalityBand, EscalationMatrixStatus } from '../types/escalationMatrix.types';
import type { EscalationContactRole } from '../types/escalationContact.types';
import { getRoleMeta } from './escalationContact.constants';

export const MAX_ESCALATION_LEVELS = 4;

export const CRITICALITY_BANDS: {
  value: CriticalityBand;
  label: string;
  chainTitle: string;
  badgeClass: string;
}[] = [
  {
    value: 'Major',
    label: 'Major',
    chainTitle: 'Major criticality chain',
    badgeClass: 'esc-criticality-badge--major',
  },
  {
    value: 'Minor',
    label: 'Minor',
    chainTitle: 'Minor criticality chain',
    badgeClass: 'esc-criticality-badge--minor',
  },
];

export const ROLE_SHORT_LABELS: Record<EscalationContactRole, string> = {
  Junior: 'Junior',
  Consultant: 'Consultant',
  HOD: 'HOD',
  'On-call': 'On-call',
};

const DEFAULT_EMPTY_CHAIN_LEVELS: {
  role: EscalationContactRole | '';
  timerMinutes: number;
  isOptional?: boolean;
}[] = [{ role: '', timerMinutes: 0, isOptional: true }];

export function getDefaultReminderMinutes(band: CriticalityBand): number {
  return band === 'Major' ? 5 : 10;
}

export function getDefaultMaxReminderAttempts(): number {
  return 3;
}

export function buildDefaultChainLevels(_band: CriticalityBand) {
  return DEFAULT_EMPTY_CHAIN_LEVELS.map((item, index) => ({
    levelNumber: index + 1,
    orderIndex: index + 1,
    role: item.role,
    timerMinutes: item.timerMinutes,
    isOptional: Boolean(item.isOptional),
  }));
}

export const MATRIX_STATUS_META: Record<
  EscalationMatrixStatus,
  { label: string; badgeClass: string; description: string }
> = {
  Draft: {
    label: 'Draft',
    badgeClass: 'esc-matrix-status--draft',
    description: 'Saved but not yet live for new critical events',
  },
  Active: {
    label: 'Active',
    badgeClass: 'esc-matrix-status--active',
    description: 'Currently used for new critical escalations',
  },
  Superseded: {
    label: 'Superseded',
    badgeClass: 'esc-matrix-status--superseded',
    description: 'Previously active configuration retained for in-flight escalations',
  },
};

export function formatChainPath(roles: (EscalationContactRole | '' | null | undefined)[]): string {
  const labels = roles
    .filter(Boolean)
    .map(
      role =>
        ROLE_SHORT_LABELS[role as EscalationContactRole] ||
        getRoleMeta(role as EscalationContactRole).label
    );
  return labels.length ? labels.join(' → ') : '—';
}

export function getPathRolesFromLevels(
  levels: { role: EscalationContactRole | ''; isOptional?: boolean }[]
): EscalationContactRole[] {
  return levels
    .filter(level => Boolean(level.role) && !(level.isOptional && !level.role))
    .map(level => level.role as EscalationContactRole);
}
